// What a chat element sends and reads (impire-hq 02-DESIGN/14-solutions/
// 03-the-console.md § the elements, *chat*; 02-DESIGN/19-chat/
// 01-the-chat-solution.md § the verbs; hq decision 0083): the models verb's
// answer, the transcript verb's body — the thread, the text, the instance —
// and the headers its first frame and its terminator carry, the threads and
// the thread verbs' answers. The assistant keeps the threads and fits the
// transcript; the console keeps none of it. Mirrors the design until
// impire-chat carries a wire file. Pure, so that the shapes are tested
// without a connection.

/** Profile is what a model offers, as the models verb answers it from the instance's verified profile. */
export interface Profile {
  efforts: string[];
  budget: boolean;
  tools: boolean;
  images: boolean;
  /** The model's limits; 0 is unknown. */
  max_input_tokens: number;
  max_output_tokens: number;
  /** When the self-test last confirmed the profile; empty is not verified. */
  verified: string;
}

/** Model is one chat-capable instance the person may talk to. */
export interface Model {
  /** The workload id of the instance: what the transcript verb names. */
  workload: string;
  /** The workspace's name for the model. */
  name: string;
  display_name: string;
  provider: string;
  model: string;
  profile: Profile;
  /** Whether a new thread starts with it. */
  default: boolean;
}

export interface ModelsReply {
  models: Model[];
}

/** SendBody is what the transcript verb takes. */
export interface SendBody {
  /** The thread's id; empty for a new thread, which the assistant mints and names on the first frame. */
  thread: string;
  /** The person's text; empty for a resend, which sends the same transcript again and appends nothing. */
  text: string;
  /** The workload id of the instance the thread talks to. */
  instance: string;
  /** The provider's sampling parameters, within what the profile offers; none by default. */
  params: Record<string, unknown>;
}

/** sendBody is a turn: the person's text on a thread, or on a new one where the id is empty. */
export function sendBody(thread: string, text: string, instance: string): SendBody {
  return { thread, text, instance, params: {} };
}

/** resendBody is the same transcript sent again on a thread, nothing appended: the retry that redeems a grant. */
export function resendBody(thread: string, instance: string): SendBody {
  return { thread, text: "", instance, params: {} };
}

/** The headers the assistant puts on the first frame of a turn. */
export const HEADER_CHAT_THREAD = "Impire-Chat-Thread";
export const HEADER_CHAT_LEFT_OUT = "Impire-Chat-Left-Out";

/** The inference contract's headers the terminator carries. */
export const HEADER_MODEL = "Impire-Infer-Model";
export const HEADER_STOP_REASON = "Impire-Infer-Stop-Reason";
export const HEADER_USAGE_INPUT = "Impire-Infer-Usage-Input-Tokens";
export const HEADER_USAGE_OUTPUT = "Impire-Infer-Usage-Output-Tokens";
export const HEADER_USAGE_CACHED = "Impire-Infer-Usage-Cached-Tokens";
export const HEADER_USAGE_COST = "Impire-Infer-Usage-Cost";

/** Usage is what a turn cost, as the instance reported it. */
export interface Usage {
  input_tokens: number;
  output_tokens: number;
  cached_tokens?: number;
  /** The provider's reported cost, as the header carries it, where it reported one. */
  cost?: string;
}

/** Outcome is the terminator's: the model that answered, why it stopped, what it cost. */
export interface Outcome {
  model: string;
  stop_reason: string;
  usage: Usage;
}

/** A minimal view of message headers: what nats-core's MsgHdrs and a plain map both offer. */
export interface HeaderReader {
  get(key: string): string;
}

function int(s: string): number {
  const n = Number.parseInt(s, 10);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

/** outcomeOf reads the outcome from a terminator's headers; zero values where absent. */
export function outcomeOf(h: HeaderReader | undefined): Outcome {
  if (h === undefined) {
    return { model: "", stop_reason: "", usage: { input_tokens: 0, output_tokens: 0 } };
  }
  // The model header is `<provider model>;<name>`; the model is what is shown.
  const model = h.get(HEADER_MODEL).split(";")[0] ?? "";
  const usage: Usage = {
    input_tokens: int(h.get(HEADER_USAGE_INPUT)),
    output_tokens: int(h.get(HEADER_USAGE_OUTPUT)),
  };
  const cached = int(h.get(HEADER_USAGE_CACHED));
  if (cached > 0) {
    usage.cached_tokens = cached;
  }
  const cost = h.get(HEADER_USAGE_COST);
  if (cost !== "") {
    usage.cost = cost;
  }
  return { model, stop_reason: h.get(HEADER_STOP_REASON), usage };
}

/** Turn is one turn of a thread, as the thread verb answers it. */
export interface Turn {
  /** The turn's place in the thread. */
  n: number;
  role: "user" | "assistant";
  text: string;
  /** When, RFC 3339. */
  at: string;
  /** The instance an assistant turn came from, by workload id. */
  model?: string;
  usage?: Usage;
  /** Whether the person stopped the reply before it ended; what arrived stands. */
  cancelled?: boolean;
  /** Whether the connection ended under the reply; what arrived stands. */
  lost?: boolean;
}

/** Summary is one thread as the threads verb lists it. */
export interface Summary {
  id: string;
  title: string;
  /** The instance it talks to, by workload id. */
  model: string;
  /** RFC 3339. */
  created: string;
  updated: string;
}

/** ThreadsReply is the threads verb's answer, newest first. */
export interface ThreadsReply {
  threads: Summary[];
}

/** Thread is one thread with its turns in order, as the thread verb answers it. */
export interface Thread extends Summary {
  turns: Turn[];
}

/** RenameBody renames a thread on the thread verb. */
export interface RenameBody {
  title: string;
}

/** DeleteBody deletes a thread on the thread verb. */
export interface DeleteBody {
  delete: true;
}

/** ClearReply is the clear verb's answer: how many threads were deleted. */
export interface ClearReply {
  deleted: number;
}

function isRecord(x: unknown): x is Record<string, unknown> {
  return typeof x === "object" && x !== null;
}

function str(x: unknown): string {
  return typeof x === "string" ? x : "";
}

function num(x: unknown): number {
  return typeof x === "number" && Number.isFinite(x) ? x : 0;
}

function readSummary(x: unknown): Summary | undefined {
  if (!isRecord(x) || typeof x.id !== "string" || x.id === "") {
    return undefined;
  }
  return {
    id: x.id,
    title: str(x.title),
    model: str(x.model),
    created: str(x.created),
    updated: str(x.updated) || str(x.created),
  };
}

function readTurn(x: unknown): Turn | undefined {
  if (!isRecord(x)) {
    return undefined;
  }
  const role = x.role === "assistant" ? "assistant" : x.role === "user" ? "user" : undefined;
  if (role === undefined) {
    return undefined;
  }
  const t: Turn = { n: num(x.n), role, text: str(x.text), at: str(x.at) };
  if (typeof x.model === "string" && x.model !== "") {
    t.model = x.model;
  }
  if (isRecord(x.usage)) {
    t.usage = { input_tokens: num(x.usage.input_tokens), output_tokens: num(x.usage.output_tokens) };
    if (typeof x.usage.cached_tokens === "number") {
      t.usage.cached_tokens = x.usage.cached_tokens;
    }
    if (typeof x.usage.cost === "string" && x.usage.cost !== "") {
      t.usage.cost = x.usage.cost;
    }
  }
  if (x.cancelled === true) {
    t.cancelled = true;
  }
  if (x.lost === true) {
    t.lost = true;
  }
  return t;
}

/** readThreads reads the threads verb's answer; an entry that cannot be read is left out rather than failing the panel. */
export function readThreads(x: unknown): Summary[] {
  if (!isRecord(x) || !Array.isArray(x.threads)) {
    return [];
  }
  return x.threads.map(readSummary).filter((t): t is Summary => t !== undefined);
}

/** readThread reads the thread verb's answer, its turns in order; undefined where it names no thread. */
export function readThread(x: unknown): Thread | undefined {
  const sum = readSummary(x);
  if (sum === undefined || !isRecord(x)) {
    return undefined;
  }
  const turns = Array.isArray(x.turns) ? x.turns.map(readTurn).filter((t): t is Turn => t !== undefined) : [];
  turns.sort((a, b) => a.n - b.n);
  return { ...sum, turns };
}

/** leftOutOf reads how many older turns the assistant did not send, from the first frame's header; 0 where absent. */
export function leftOutOf(h: HeaderReader | undefined): number {
  return h === undefined ? 0 : int(h.get(HEADER_CHAT_LEFT_OUT));
}

/**
 * written reports whether the thread holds the person's turn as its last: the
 * assistant wrote it before the first frame, so the retry is the resend —
 * empty text — and not the turn again, which would append it twice.
 */
export function written(t: Thread | undefined, text: string): boolean {
  const last = t?.turns[t.turns.length - 1];
  return last !== undefined && last.role === "user" && last.text === text;
}
